import { addDays, isBefore, isSameDay, startOfDay } from 'date-fns';
import { format } from 'date-fns/fp';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { AlertCircle, Bell, CheckCircleIcon } from 'lucide-react';

import { ListFindingsThroughputPerDay } from '@/alerts/view/queries/list-findings-throughput-per-day';
import { Loading } from '@/components/ui/old/Loading';
import { useNow } from '@/hooks/useNow';

import { ChartCardLayout } from './chart-card-layout';

const RANGE_IN_DAYS = 30;

const formatDateLabel = format('MMM d');

enum FindingStatus {
  CRITICAL = 'Critical',
  WARNING = 'Warning',
}

const colorFromFindingStatus: Record<FindingStatus, string> = {
  [FindingStatus.CRITICAL]: '#ef4444',
  [FindingStatus.WARNING]: '#eab308',
};

const zIndexFromFindingStatus: Record<FindingStatus, number> = {
  [FindingStatus.CRITICAL]: 3,
  [FindingStatus.WARNING]: 2,
};

type FindingPoint = {
  date: Date;
  numbers: Record<FindingStatus, number>;
};

function* generateDateRange(start: Date, end: Date): Generator<Date> {
  for (
    let date = start;
    isBefore(date, end) || isSameDay(date, end);
    date = addDays(date, 1)
  ) {
    yield date;
  }
}

function pointsFromFindingsThroughputPerDay(
  points: ListFindingsThroughputPerDay.Output,
  now: Date,
): FindingPoint[] {
  const end = startOfDay(now);
  const start = addDays(end, -RANGE_IN_DAYS + 1);
  const dateRange = Array.from(generateDateRange(start, end));

  const pointsByDay = new Map(
    points.map((point) => [startOfDay(point.date).getTime(), point] as const),
  );

  return dateRange.map((date) => {
    const key = startOfDay(date).getTime();
    const point = pointsByDay.get(key);

    return {
      date,
      numbers: {
        [FindingStatus.CRITICAL]: point?.critical ?? 0,
        [FindingStatus.WARNING]: point?.warning ?? 0,
      },
    };
  });
}

function hasAnyAlertInData(data: FindingPoint[]): boolean {
  return data.some((point) =>
    Object.values(point.numbers).some((count) => count > 0),
  );
}

export function FindingsChart() {
  const now = useNow();
  const {
    data: findingsThroughputPerDay,
    isLoading,
    isError,
  } = ListFindingsThroughputPerDay.useQuery({
    daysRange: RANGE_IN_DAYS,
  });

  const data = findingsThroughputPerDay
    ? pointsFromFindingsThroughputPerDay(findingsThroughputPerDay, now)
    : undefined;

  const hasAlerts = data ? hasAnyAlertInData(data) : false;

  return (
    <ChartCardLayout
      title="Alerts"
      featureFlagKey="alerts"
      linkTo="/alerts"
      buttonIcon={<Bell />}
      buttonLabel="View Alerts"
      showButton={hasAlerts}
    >
      <div data-testid="findings-chart" className="w-full">
        {isLoading ? (
          <Loading />
        ) : isError || !data ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-600">
            <AlertCircle className="size-6 text-red-500" />
            <p className="text-normal font-medium">Unable to load alerts</p>
            <p className="text-sm text-gray-500">
              Please refresh the page or contact support if the issue persists.
            </p>
          </div>
        ) : (
          <FindingsChartContent data={data} />
        )}
      </div>
    </ChartCardLayout>
  );
}

type FindingsChartContentProps = {
  data: FindingPoint[];
};

function FindingsChartContent({ data }: FindingsChartContentProps) {
  const hasAnyAlert = hasAnyAlertInData(data);

  if (!hasAnyAlert) {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center gap-1">
        <CheckCircleIcon className="mb-1 size-8 text-primary-600" />
        <p className="text-base font-medium text-primary-600">
          All clear! No active alerts.
        </p>
        <p className="text-sm text-gray-400">
          We'll show alerts here when issues arise.
        </p>
      </div>
    );
  }

  const chartOptions: Highcharts.Options = {
    title: { style: { display: 'none' } },
    plotOptions: { series: { marker: { enabled: false }, animation: false } },
    tooltip: { enabled: true, shared: true, split: false },
    yAxis: { title: { text: 'Number of Active Alerts' } },
    xAxis: { categories: data.map(({ date }) => formatDateLabel(date)) },
    series: Object.values(FindingStatus).map((status) => ({
      type: 'line',
      name: status,
      marker: { symbol: 'circle' },
      color: colorFromFindingStatus[status],
      zIndex: zIndexFromFindingStatus[status],
      data: data.map(({ numbers }) => numbers[status]),
    })),
    chart: { renderTo: 'container', height: '50%' },
    legend: { enabled: false },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 400,
          },
          chartOptions: {
            xAxis: {
              labels: {
                enabled: false,
              },
              title: {
                text: null,
              },
            },
            yAxis: {
              labels: {
                enabled: false,
              },
              title: {
                text: null,
              },
            },
          },
        },
      ],
    },
    credits: { enabled: false },
  };

  return (
    <HighchartsReact
      highcharts={Highcharts}
      className="w-full"
      options={chartOptions}
    />
  );
}
